import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getFunctionLogs, listFunctions, type DeployedFunction, type FunctionRole, type LogLine, type LogTail, type LogWindow, type MailboxInfo } from "../api";
import { formatTimestamp } from "../format";

// The functions' logs, read from CloudWatch through the inspector: what the
// poller did on the hour, what the processor made of a file, what the API
// answered — without AWS credentials and without leaving this page. A tail,
// newest first, over a window, read a page at a time: reaching the bottom reads
// the page of older lines below it, until the window is exhausted. The
// narrowing (a level, a word) is done here in the browser over the lines
// already fetched, so what is shown is always a subset of what was read, never
// a different read.

const WINDOWS: ReadonlyArray<{ id: LogWindow; label: string }> = [
  { id: "15m", label: "15 min" },
  { id: "1h", label: "1 hour" },
  { id: "6h", label: "6 hours" },
  { id: "24h", label: "24 hours" },
  { id: "7d", label: "7 days" },
];

type LevelFilter = "all" | "warn" | "error";

/** Lines per page. Enough to fill a screen several times over; cheap to read again. */
const PAGE = 200;

/** What has been read so far, and where the next page starts. */
interface Read {
  /** The pages so far, newest first. */
  lines: LogLine[];
  /** The window every page reads. */
  since: string;
  until: string;
  exists: boolean;
  /** Where the next page starts; null once the window has been read to its start. */
  nextCursor: string | null;
}

type Tail =
  | { state: "reading" }
  | { state: "read"; read: Read; more: "idle" | "reading" | { error: string } }
  | { state: "failed"; error: string };

const ROLE_TITLES: Record<FunctionRole, string> = {
  api: "REST API",
  inbound: "Inbound processor",
  inspector: "File inspector (this)",
  poll: "SFTP poller",
  upload: "SFTP uploader",
  watch: "Delivery watcher",
};

/** "SFTP poller · BS2" — by the subsystem the bank knows the login by. */
function titleOf(fn: DeployedFunction, mailboxes: MailboxInfo[]): string {
  const title = ROLE_TITLES[fn.role];
  if (fn.mailbox === undefined) return title;
  const info = mailboxes.find((m) => m.id === fn.mailbox);
  return `${title} · ${info?.subsystem ?? fn.mailbox}`;
}

// Powertools' own envelope, shown once in the row rather than repeated per line.
const ENVELOPE = new Set(["level", "message", "timestamp", "service", "sampling_rate", "xray_trace_id"]);

interface ParsedLine {
  level?: string;
  text: string;
  fields: Array<[string, string]>;
  /** A START / END / REPORT line from the Lambda runtime, not from the service. */
  runtime: boolean;
}

function parseLine(message: string): ParsedLine {
  if (message.startsWith("{")) {
    try {
      const parsed: unknown = JSON.parse(message);
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
        const record = parsed as Record<string, unknown>;
        const level = typeof record.level === "string" ? record.level : undefined;
        const text = typeof record.message === "string" ? record.message : message;
        const fields = Object.entries(record)
          .filter(([key]) => !ENVELOPE.has(key))
          .map(([key, value]): [string, string] => [key, typeof value === "string" ? value : JSON.stringify(value)]);
        return { level, text, fields, runtime: false };
      }
    } catch {
      // Not JSON after all; shown as written.
    }
  }
  return { text: message, fields: [], runtime: /^(START|END|REPORT|INIT_START|INIT_REPORT)\b/.test(message) };
}

const rank = (level: string | undefined): number =>
  level === "ERROR" || level === "CRITICAL" ? 2 : level === "WARN" || level === "WARNING" ? 1 : 0;

const message = (err: unknown): string => (err instanceof Error ? err.message : String(err));

export function FunctionLogs({ token, mailboxes }: { token: string; mailboxes: MailboxInfo[] }): JSX.Element {
  const [functions, setFunctions] = useState<DeployedFunction[] | undefined>();
  const [listError, setListError] = useState<string | undefined>();
  const [selected, setSelected] = useState<string | undefined>();
  const [window, setWindow] = useState<LogWindow>("1h");
  const [level, setLevel] = useState<LevelFilter>("all");
  const [needle, setNeedle] = useState("");
  const [tail, setTail] = useState<Tail | undefined>();
  const [refresh, setRefresh] = useState(0);
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    listFunctions(token)
      .then((list) => {
        if (cancelled) return;
        setFunctions(list);
        setSelected((current) => current ?? list[0]?.id);
      })
      .catch((err: unknown) => {
        if (!cancelled) setListError(message(err));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // The first page: on every change of function or window, and on "read again".
  useEffect(() => {
    if (selected === undefined) return;
    let cancelled = false;
    setTail({ state: "reading" });
    getFunctionLogs(token, selected, { window, limit: PAGE })
      .then((page) => {
        if (!cancelled) setTail({ state: "read", read: asRead(page), more: "idle" });
      })
      .catch((err: unknown) => {
        if (!cancelled) setTail({ state: "failed", error: message(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [token, selected, window, refresh]);

  const reload = useCallback(() => setRefresh((n) => n + 1), []);

  // The next page: the lines older than the oldest one shown, appended. Asked
  // for when the reader reaches the bottom, or by the button there.
  const cursor = tail?.state === "read" && tail.more === "idle" ? tail.read.nextCursor : null;
  const loadOlder = useCallback(() => {
    if (selected === undefined || cursor === null) return;
    setTail((current) => (current?.state === "read" ? { ...current, more: "reading" } : current));
    getFunctionLogs(token, selected, { window, limit: PAGE, cursor })
      .then((page) => {
        // Appended only onto the read it continues — not onto a fresh first
        // page the reader asked for meanwhile.
        setTail((current) =>
          current?.state === "read" && current.read.nextCursor === cursor
            ? { state: "read", read: { ...current.read, lines: [...current.read.lines, ...page.lines], nextCursor: page.nextCursor }, more: "idle" }
            : current,
        );
      })
      .catch((err: unknown) => {
        setTail((current) =>
          current?.state === "read" && current.read.nextCursor === cursor ? { ...current, more: { error: message(err) } } : current,
        );
      });
  }, [token, selected, window, cursor]);

  // Reaching the bottom asks for the next page. The sentinel sits under the last
  // line; when it comes into view and there is more to read, it is read.
  useEffect(() => {
    const target = sentinel.current;
    if (!target || cursor === null) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadOlder();
      },
      { rootMargin: "0px 0px 240px 0px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [cursor, loadOlder]);

  const lines = useMemo(() => {
    if (tail?.state !== "read") return [];
    const wanted = needle.trim().toLowerCase();
    return tail.read.lines
      .map((line) => ({ line, parsed: parseLine(line.message) }))
      .filter(({ parsed }) => level === "all" || rank(parsed.level) >= (level === "warn" ? 1 : 2))
      .filter(({ line }) => wanted === "" || line.message.toLowerCase().includes(wanted));
  }, [tail, level, needle]);

  if (listError) return <p className="error">The functions could not be listed: {listError}</p>;
  if (functions === undefined) return <p className="admin-status">Loading…</p>;
  if (functions.length === 0) {
    return <p className="admin-status">Nothing is deployed here, so there are no logs to read.</p>;
  }

  return (
    <>
      <div className="log-controls">
        <label className="log-control">
          <span className="label">Function</span>
          <select value={selected} onChange={(event) => setSelected(event.target.value)}>
            {functions.map((fn) => (
              <option key={fn.id} value={fn.id}>
                {titleOf(fn, mailboxes)}
              </option>
            ))}
          </select>
        </label>
        <div className="log-control">
          <span className="label">Window</span>
          <div className="segmented" role="group" aria-label="How far back to look">
            {WINDOWS.map((option) => (
              <button key={option.id} type="button" aria-pressed={option.id === window} onClick={() => setWindow(option.id)}>
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <div className="log-control">
          <span className="label">Show</span>
          <div className="segmented" role="group" aria-label="Which levels to show">
            <button type="button" aria-pressed={level === "all"} onClick={() => setLevel("all")}>
              Everything
            </button>
            <button type="button" aria-pressed={level === "warn"} onClick={() => setLevel("warn")}>
              Warnings and errors
            </button>
            <button type="button" aria-pressed={level === "error"} onClick={() => setLevel("error")}>
              Errors
            </button>
          </div>
        </div>
        <label className="log-control log-search">
          <span className="label">Containing</span>
          <input type="search" value={needle} placeholder="a file name, a word" onChange={(event) => setNeedle(event.target.value)} />
        </label>
        <button type="button" className="button log-refresh" onClick={reload} disabled={tail?.state === "reading"}>
          {tail?.state === "reading" ? "Reading…" : "Read again"}
        </button>
      </div>

      <LogStatus tail={tail} shown={lines.length} />

      {tail?.state === "read" && tail.read.exists && lines.length > 0 ? (
        <div className="console log-console">
          <ol className="log-lines">
            {lines.map(({ line, parsed }, index) => (
              <LogRow key={`${line.timestamp}-${index}`} line={line} parsed={parsed} />
            ))}
          </ol>
        </div>
      ) : null}

      {tail?.state === "read" && tail.read.exists ? (
        <div className="log-more" ref={sentinel}>
          <LogMore tail={tail} onMore={loadOlder} />
        </div>
      ) : null}
    </>
  );
}

const asRead = (page: LogTail): Read => ({
  lines: page.lines,
  since: page.since,
  until: page.until,
  exists: page.exists,
  nextCursor: page.nextCursor,
});

function LogStatus({ tail, shown }: { tail: Tail | undefined; shown: number }): JSX.Element | null {
  if (tail === undefined) return null;
  if (tail.state === "reading") return <p className="admin-status">Reading…</p>;
  if (tail.state === "failed") return <p className="error">The logs could not be read: {tail.error}</p>;
  if (!tail.read.exists) return <p className="admin-status">This function has never run — it has no log group yet.</p>;
  const total = tail.read.lines.length;
  const read = `Read ${formatTimestamp(tail.read.until)} · since ${formatTimestamp(tail.read.since)}`;
  if (total === 0) return <p className="admin-status">{read} · nothing was written in this window.</p>;
  return (
    <p className="admin-status">
      {read} · {shown === total ? `${total} line${total === 1 ? "" : "s"}` : `${shown} of ${total} lines`}
      {tail.read.nextCursor !== null ? " so far · older lines load as you scroll" : " · the whole window"}
    </p>
  );
}

/** The foot of the list: reading, read to the start, or a button in case the scroll did not ask. */
function LogMore({ tail, onMore }: { tail: Extract<Tail, { state: "read" }>; onMore: () => void }): JSX.Element | null {
  if (tail.read.lines.length === 0) return null;
  if (tail.more === "reading") return <p className="admin-status">Reading older lines…</p>;
  if (typeof tail.more === "object") {
    return (
      <>
        <p className="error">Older lines could not be read: {tail.more.error}</p>
        <button type="button" className="linkish" onClick={onMore}>
          Try again
        </button>
      </>
    );
  }
  if (tail.read.nextCursor === null) return <p className="admin-status">That is the whole window.</p>;
  return (
    <button type="button" className="linkish" onClick={onMore}>
      Load older lines
    </button>
  );
}

const time = new Intl.DateTimeFormat("da-DK", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
const day = new Intl.DateTimeFormat("da-DK", { day: "2-digit", month: "2-digit" });

function LogRow({ line, parsed }: { line: LogLine; parsed: ParsedLine }): JSX.Element {
  const at = new Date(line.timestamp);
  const millis = String(at.getMilliseconds()).padStart(3, "0");
  return (
    <li className="log-line" data-runtime={parsed.runtime} data-level={parsed.level?.toLowerCase()}>
      <span className="log-time" title={line.stream}>
        {day.format(at)} {time.format(at)}.{millis}
      </span>
      <span className="log-level">{parsed.level ?? (parsed.runtime ? "runtime" : "")}</span>
      <span className="log-text">
        {parsed.text}
        {parsed.fields.length > 0 ? (
          <details className="log-fields">
            <summary>{parsed.fields.length} field{parsed.fields.length === 1 ? "" : "s"}</summary>
            <dl>
              {parsed.fields.map(([key, value]) => (
                <div key={key}>
                  <dt>{key}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </details>
        ) : null}
      </span>
    </li>
  );
}
